import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { toast } from "sonner";
import { LogIn } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      const r = res.user.role;
      toast.success(`Welcome, ${res.user.name}`);
      nav(r === "admin" ? "/admin" : r === "surveyor" ? "/surveyor" : "/candidate", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* left panel */}
      <div className="hidden lg:flex relative bg-slate-900 text-white p-12 flex-col justify-between overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1459787827056-fca21e4b5b17?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
          }}
        />
        <div className="absolute inset-0 wi-grid-bg opacity-30" />
        <div className="relative">
          <Brand invert size="lg" />
          <div className="mt-2 data-label text-blue-300">
            Election Intelligence Platform
          </div>
        </div>
        <div className="relative">
          <h2 className="text-3xl xl:text-4xl font-semibold tracking-tight leading-tight max-w-md">
            Door-knocked data.
            <br />
            <span className="text-blue-400">Booth-level truth.</span>
          </h2>
          <p className="mt-4 text-slate-400 text-sm max-w-md leading-relaxed">
            Three roles. One platform. Admins manage wards & forms,
            surveyors collect ground-truth data, candidates unlock
            ward-level analytics.
          </p>
        </div>
        <div className="relative data-label text-slate-500">
          v1.0 · Bengaluru BBMP
        </div>
      </div>

      {/* right panel */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Brand size="md" />
          </div>
          <div className="data-label mb-2">Sign in</div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            Welcome back
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Single login. We'll route you to the right console based on
            your role.
          </p>

          <form className="mt-8 space-y-4" onSubmit={onSubmit}>
            <label className="block">
              <span className="block text-sm font-medium text-slate-700 mb-1.5">
                Email
              </span>
              <input
                type="email"
                required
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-slate-700 mb-1.5">
                Password
              </span>
              <input
                type="password"
                required
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-medium py-2.5 rounded-md transition-colors"
            >
              <LogIn size={16} />
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-xs text-slate-500 border-t border-slate-100 pt-4 space-y-1 font-mono">
            <div>Candidates → <Link to="/#request" className="text-blue-700 hover:underline">request access on the landing page</Link></div>
            <div>Surveyors → credentials are issued by the admin</div>
          </div>
        </div>
      </div>
    </div>
  );
}
